// PortfolioAudienceDemographics — the Audience Demographics block inside the
// Social Accounts section (EPD-10117). Renders for the platform selected in the
// shared pill tabs (the active entry is passed in).
//
// Layout & spec follow the Creator Toolkit prototype (AudienceRow / DonutChart /
// LocationList): Age and Gender as donut charts with legend lists, and Top
// Locations as a vertical bar chart with a Countries / States / Cities toggle
// (defaults to Cities). Chart colours derive from a six-step ramp blending the
// theme accent toward the background via color-mix(), so they re-theme live.
//
// Phones (below the renderer's `@[30rem]/portfolio:` breakpoint, EPD-11532):
// Age and Gender stack in one column, each legend wraps under its donut when
// the card is too narrow, and Top Locations switches to horizontal bars.
//
// Data comes from channel.getSocialStats (EPD-10112): ageData (age → value),
// genderAgeData (age → gender → value), and country/state/cityData ([label,
// value] tuples). Values may be counts, fractions or percentages depending on
// the platform, so every distribution is normalised to a share for display.

import type { Platform, SocialStatsEntry } from "@/lib/queries/channel";
import type { DemographicKey } from "@/lib/portfolio/metric-catalog";
import { getPlatformLabel } from "@/components/channel/platform-utils";
import { useState } from "react";
import { z } from "zod";
import { VerifiedLabel } from "./portfolio-verified-badge";

// --- theme ramp -------------------------------------------------------------
// Accent blended toward the page background; blend% 0 = pure accent.
function rampColor(blendPct: number): string {
  return `color-mix(in srgb, var(--pf-accent) ${100 - blendPct}%, var(--pf-bg))`;
}
// Seven steps: age distributions can have up to seven buckets, so each gets a
// distinct tone rather than the last two sharing a colour.
const RAMP = [0, 18, 34, 50, 64, 77, 88].map(rampColor);
// Gender uses a three-colour subset (matching the prototype's GenderBar palette):
// accent, ramp[2] (40% blend) and ramp[4] (70% blend).
const GENDER_RAMP = [RAMP[0], RAMP[2], RAMP[4]];

function colorAt(colors: string[], i: number): string {
  return colors[Math.min(i, colors.length - 1)];
}

// --- data parsing -----------------------------------------------------------
const numberRecord = z.record(z.string(), z.number());
const genderAgeSchema = z.record(z.string(), numberRecord);
const tupleListSchema = z.array(z.array(z.union([z.string(), z.number()])));

interface Slice {
  label: string;
  pct: number;
  sublabel?: string;
}

// Share of the total, for an exhaustive distribution (age, gender): each value
// as a percentage of the sum, so the parts always add to 100 regardless of the
// source unit (counts, fractions or percentages).
function sharePercents(values: number[]): number[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  return total > 0 ? values.map((v) => (v / total) * 100) : values.map(() => 0);
}

// For a non-exhaustive list (top locations) where the values may already be true
// audience percentages: keep fractions/percentages as-is, only normalise raw
// counts, so a "top 5" isn't inflated to sum to 100.
function toPercents(values: number[]): number[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return values.map(() => 0);
  const max = Math.max(...values);
  if (max <= 1) return values.map((v) => v * 100); // fractions
  if (total <= 101) return values; // already percentages
  return values.map((v) => (v / total) * 100); // counts
}

// Order age buckets by the first number in the label so any bucketing sorts
// correctly ("13-17", "18-24", …, "45-64", "65-", "65+"). A label with no
// leading number sorts last.
function ageStart(label: string): number {
  const n = Number.parseInt(label, 10);
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}

function ageSlices(raw: SocialStatsEntry["stats"]): Slice[] {
  const parsed = numberRecord.safeParse(raw?.ageData);
  if (!parsed.success) return [];
  const entries = Object.entries(parsed.data).sort(
    (a, b) => ageStart(a[0]) - ageStart(b[0]),
  );
  const pcts = sharePercents(entries.map(([, v]) => v));
  return entries
    .map(([label], i) => ({ label, pct: pcts[i] }))
    .filter((s) => s.pct > 0);
}

function genderLabel(key: string): string {
  const upper = key.toUpperCase();
  if (upper.startsWith("F")) return "Female";
  if (upper.startsWith("M")) return "Male";
  return "Other";
}

function genderSlices(raw: SocialStatsEntry["stats"]): Slice[] {
  const parsed = genderAgeSchema.safeParse(raw?.genderAgeData);
  if (!parsed.success) return [];
  // Sum each gender across all age buckets.
  const totals = new Map<string, number>();
  for (const byGender of Object.values(parsed.data)) {
    for (const [gender, value] of Object.entries(byGender)) {
      const label = genderLabel(gender);
      totals.set(label, (totals.get(label) ?? 0) + value);
    }
  }
  const order = ["Female", "Male", "Other"];
  const entries = [...totals.entries()].sort(
    (a, b) => order.indexOf(a[0]) - order.indexOf(b[0]),
  );
  const pcts = sharePercents(entries.map(([, v]) => v));
  return entries
    .map(([label], i) => ({ label, pct: pcts[i] }))
    .filter((s) => s.pct > 0);
}

// A location tuple is [label, value] and, for cities, may carry a country code
// as a second string (or embedded as "City, CC").
function locationSlices(
  raw: Record<string, unknown> | null | undefined,
  withSublabel: boolean,
): Slice[] {
  const parsed = tupleListSchema.safeParse(raw);
  if (!parsed.success) return [];
  const rows = parsed.data.map((tuple) => {
    let label: string | undefined;
    let code: string | undefined;
    let value: number | undefined;
    for (const cell of tuple) {
      if (typeof cell === "number") {
        if (value === undefined) value = cell;
      } else if (label === undefined) {
        label = cell;
      } else if (code === undefined) {
        code = cell;
      }
    }
    return { label: label ?? "—", code, value: value ?? 0 };
  });
  const pcts = toPercents(rows.map((r) => r.value));
  return rows
    .map((r, i) => {
      let name = r.label;
      let sublabel = withSublabel ? r.code : undefined;
      if (withSublabel && !sublabel && name.includes(", ")) {
        const [city, cc] = name.split(", ");
        name = city;
        sublabel = cc;
      }
      return { label: name, pct: pcts[i], sublabel };
    })
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 5);
}

// --- presentational pieces --------------------------------------------------
function Donut({ slices, colors }: { slices: Slice[]; colors: string[] }) {
  const total = slices.reduce((sum, s) => sum + s.pct, 0);
  let acc = 0;
  const stops = slices
    .map((s, i) => {
      const start = acc;
      acc += total > 0 ? (s.pct / total) * 360 : 0;
      return `${colorAt(colors, i)} ${start}deg ${acc}deg`;
    })
    .join(", ");
  // The center reads out the largest segment, matching the prototype donut.
  const primary = slices.reduce(
    (top, s) => (s.pct > top.pct ? s : top),
    slices[0],
  );
  return (
    <div
      className="relative shrink-0"
      style={{ width: 112, height: 112 }}
      aria-hidden="true"
    >
      <div
        className="h-full w-full rounded-full"
        style={{
          background: acc > 0 ? `conic-gradient(${stops})` : "var(--pf-border)",
        }}
      />
      <div
        className="absolute flex flex-col items-center justify-center rounded-full"
        style={{ inset: "23%", background: "var(--pf-bg)" }}
      >
        <span
          className="font-bold leading-none"
          style={{
            fontFamily: "var(--pf-font-heading)",
            fontSize: 20,
            color: "var(--pf-heading)",
          }}
        >
          {Math.round(primary.pct)}%
        </span>
        <span
          className="mt-0.5 text-[9px] font-semibold uppercase"
          style={{ letterSpacing: "0.1em", color: "var(--pf-muted)" }}
        >
          {primary.label}
        </span>
      </div>
    </div>
  );
}

function Legend({ slices, colors }: { slices: Slice[]; colors: string[] }) {
  return (
    // min-w-36: when the card is too narrow for donut + legend side by side,
    // the legend wraps below the donut instead of squeezing its labels.
    <ul className="m-0 min-w-36 flex-1 list-none space-y-1.5 p-0">
      {slices.map((s, i) => (
        <li key={s.label} className="flex items-center gap-2 text-[13px]">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]"
            style={{ background: colorAt(colors, i) }}
          />
          <span
            className="min-w-0 flex-1 wrap-break-word"
            style={{ color: "var(--pf-heading)" }}
          >
            {s.label}
          </span>
          <span
            className="shrink-0 tabular-nums"
            style={{ color: "var(--pf-muted)" }}
          >
            {s.pct.toFixed(1)}%
          </span>
        </li>
      ))}
    </ul>
  );
}

function DemographicCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-2xl border"
      style={{
        borderColor: "var(--pf-border)",
        padding: "20px 22px 22px",
      }}
    >
      <div
        className="mb-4 text-[11px] font-semibold uppercase"
        style={{ letterSpacing: "0.16em", color: "var(--pf-muted)" }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

type LocationView = "countries" | "states" | "cities";
const LOCATION_VIEWS: Array<{ id: LocationView; label: string }> = [
  { id: "countries", label: "Countries" },
  { id: "states", label: "States" },
  { id: "cities", label: "Cities" },
];

const BAR_CHART_HEIGHT = 150;

function LocationBars({ slices }: { slices: Slice[] }) {
  const max = Math.max(...slices.map((s) => s.pct), 1);
  const columns = `repeat(${slices.length}, minmax(0, 1fr))`;
  // The visual chart splits each value from its label across two rows, which a
  // screen reader would read as disconnected numbers then names. Expose one
  // coherent text alternative and hide the decorative bars/labels from AT.
  const summary = slices
    .map(
      (s) =>
        `${s.label}${s.sublabel ? ` (${s.sublabel})` : ""} ${s.pct.toFixed(1)}%`,
    )
    .join(", ");
  const key = (s: Slice) => `${s.label}-${s.sublabel ?? ""}`;
  const barColor = (s: Slice, i: number) =>
    s.pct === max ? "var(--pf-accent)" : colorAt(RAMP, i + 1);
  return (
    <div role="img" aria-label={`Top locations: ${summary}`}>
      {/* Phone layout: horizontal bars (label, bar, value), so long city names
          wrap in a fixed label column instead of colliding in a 1/5-width
          column. Above the portfolio breakpoint, the vertical chart below. */}
      <ul
        aria-hidden="true"
        className="m-0 list-none space-y-2.5 p-0 @[30rem]/portfolio:hidden"
      >
        {slices.map((s, i) => (
          <li key={key(s)} className="flex items-center gap-2.5">
            <div
              className="w-24 shrink-0 wrap-break-word text-[12px] font-medium leading-tight"
              style={{ color: "var(--pf-heading)" }}
            >
              {s.label}
              {s.sublabel && (
                <span
                  className="font-normal"
                  style={{ color: "var(--pf-muted)" }}
                >
                  {" "}
                  {s.sublabel}
                </span>
              )}
            </div>
            <div className="h-2.5 min-w-0 flex-1">
              <div
                className="h-full rounded-[4px]"
                style={{
                  width: `${Math.max(2, (s.pct / max) * 100)}%`,
                  background: barColor(s, i),
                }}
              />
            </div>
            <span
              className="w-12 shrink-0 text-right text-[12px] font-semibold tabular-nums"
              style={{
                color: s.pct === max ? "var(--pf-accent)" : "var(--pf-muted)",
              }}
            >
              {s.pct.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>

      {/* Bars — each cell bottom-aligns the percentage above its bar. */}
      <div
        aria-hidden="true"
        className="hidden items-end gap-2.5 @[30rem]/portfolio:grid"
        style={{ gridTemplateColumns: columns, height: BAR_CHART_HEIGHT }}
      >
        {slices.map((s, i) => (
          <div key={key(s)} className="flex flex-col items-center">
            <span
              className="mb-1 text-[11px] font-semibold tabular-nums"
              style={{
                color: s.pct === max ? "var(--pf-accent)" : "var(--pf-muted)",
              }}
            >
              {s.pct.toFixed(1)}%
            </span>
            <div
              className="w-full rounded-[4px]"
              style={{
                height: Math.max(4, (s.pct / max) * (BAR_CHART_HEIGHT - 22)),
                background: barColor(s, i),
              }}
            />
          </div>
        ))}
      </div>
      {/* Labels row (city over country code, or a single-line region name). */}
      <div
        aria-hidden="true"
        className="mt-2 hidden gap-2.5 @[30rem]/portfolio:grid"
        style={{ gridTemplateColumns: columns }}
      >
        {slices.map((s) => (
          <div
            key={key(s)}
            className="truncate text-center text-[11px] font-medium leading-tight"
            style={{ color: "var(--pf-heading)" }}
          >
            {s.label}
            {s.sublabel && (
              <div className="font-normal" style={{ color: "var(--pf-muted)" }}>
                {s.sublabel}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Whether the entry has any demographic data to render — mirrors the block's
// own empty check so callers can hide surrounding chrome.
export function hasAudienceData(entry: SocialStatsEntry): boolean {
  const stats = entry.stats;
  return (
    ageSlices(stats).length > 0 ||
    genderSlices(stats).length > 0 ||
    locationSlices(stats?.countryData, false).length > 0 ||
    locationSlices(stats?.stateData, false).length > 0 ||
    locationSlices(stats?.cityData, true).length > 0
  );
}

interface PortfolioAudienceDemographicsProps {
  entry: SocialStatsEntry;
  // Which demographic charts the creator chose to show (EPD-10133). Undefined →
  // no selection saved, so every available chart shows. Note there is no
  // "state" metric in the picker, so the States view only appears under the
  // default (unfiltered) selection.
  enabled?: Set<DemographicKey>;
}

export function PortfolioAudienceDemographics({
  entry,
  enabled,
}: PortfolioAudienceDemographicsProps) {
  const stats = entry.stats;
  const allow = (key: DemographicKey) => !enabled || enabled.has(key);
  const age = allow("age") ? ageSlices(stats) : [];
  const gender = allow("gender") ? genderSlices(stats) : [];
  const locations: Record<LocationView, Slice[]> = {
    countries: allow("country")
      ? locationSlices(stats?.countryData, false)
      : [],
    states: enabled ? [] : locationSlices(stats?.stateData, false),
    cities: allow("city") ? locationSlices(stats?.cityData, true) : [],
  };
  const availableViews = LOCATION_VIEWS.filter(
    (v) => locations[v.id].length > 0,
  );
  // Default to Cities when available, else the first view with data.
  const defaultView =
    availableViews.find((v) => v.id === "cities")?.id ??
    availableViews[0]?.id ??
    "cities";
  const [view, setView] = useState<LocationView | null>(null);
  const activeView =
    view && availableViews.some((v) => v.id === view) ? view : defaultView;

  const hasAny =
    age.length > 0 || gender.length > 0 || availableViews.length > 0;
  if (!hasAny) return null;

  const platformLabel = getPlatformLabel(entry.platform as Platform);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 style={{ margin: 0 }}>{platformLabel} Audience Demographics</h3>
        <VerifiedLabel>Last 30 Days</VerifiedLabel>
      </div>

      {(age.length > 0 || gender.length > 0) && (
        // One column on phones; the prototype's 1.1fr / 0.9fr pair above the
        // portfolio breakpoint (see portfolio-renderer).
        <div className="grid grid-cols-1 gap-2.5 @[30rem]/portfolio:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          {age.length > 0 && (
            <DemographicCard title="Age">
              <div className="flex flex-wrap items-center gap-4">
                <Donut slices={age} colors={RAMP} />
                <Legend slices={age} colors={RAMP} />
              </div>
            </DemographicCard>
          )}
          {gender.length > 0 && (
            <DemographicCard title="Gender">
              <div className="flex flex-wrap items-center gap-4">
                <Donut slices={gender} colors={GENDER_RAMP} />
                <Legend slices={gender} colors={GENDER_RAMP} />
              </div>
            </DemographicCard>
          )}
        </div>
      )}

      {availableViews.length > 0 && (
        <div
          className="rounded-2xl border"
          style={{ borderColor: "var(--pf-border)", padding: "20px 22px 22px" }}
        >
          {/* Title and the Countries/States/Cities toggle share one row; on a
              narrow card the toggle wraps below the title. */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div
              className="text-[11px] font-semibold uppercase"
              style={{ letterSpacing: "0.16em", color: "var(--pf-muted)" }}
            >
              Top Locations
            </div>
            <div
              role="group"
              aria-label="Location breakdown"
              className="inline-flex gap-1 rounded-[8px] p-0.5"
              style={{ background: "var(--pf-surface)" }}
            >
              {availableViews.map((v) => {
                const isActive = v.id === activeView;
                return (
                  <button
                    key={v.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setView(v.id)}
                    // min-h-8: a 32px touch target on phones; the compact
                    // prototype height above the breakpoint.
                    className="min-h-8 rounded-[6px] px-3 py-1 text-[11px] font-medium transition-colors @[30rem]/portfolio:min-h-0 @[30rem]/portfolio:px-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    style={{
                      background: isActive ? "var(--pf-bg)" : "transparent",
                      color: isActive ? "var(--pf-heading)" : "var(--pf-muted)",
                      boxShadow: isActive
                        ? "0 1px 2px rgba(15,19,17,0.1)"
                        : "none",
                    }}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
          </div>
          <LocationBars slices={locations[activeView]} />
        </div>
      )}
    </section>
  );
}
